import { createInstance } from '@/helpers/api/base'
import { format } from 'date-fns'
import { useAppQuery } from '@/helpers/api/query'
import { useGarageStore } from '@/modules/FindGarage'
import { useMemo } from 'react'
import { Role } from '@/types/auth'
import { useAuthStore } from '@/store/auth-store'
import { useWorkstationStore } from '@/modules/Workstation/store'
import { useGetHubs } from '@/modules/Workstation/api/useGetHubs'
import { routeSorter } from '@/pages/Dashboard/helpers/sortRoutes'

interface IPeriod {
    dayOfWeek: string
    day: number
    date: string
    isToday: boolean
    summary: {
        isLocked: boolean
        unfinishedCount: number
        activeFlagCount: number
        doneFlagCount: number
        isEmpty: boolean
    }
}

interface IMileagePeriodResponse {
    today: string
    periods: IPeriod[]
}

export const useGetMileagePeriod = () => {
    const date = new Date()
    const formattedDate = format(date, 'yyyy/MM/dd')
    const { garage } = useGarageStore()
    const { role } = useAuthStore()
    const { workstation } = useWorkstationStore()

    // PM role always uses default hub routes regardless of date
    const isPM = role === Role.PerformanceManager
    const { data: defaultHubs } = useGetHubs(isPM ? true : undefined)

    // Get routes from default hubs for PM role
    const defaultRoutes = useMemo(() => {
        if (!isPM || !defaultHubs || !workstation) {
            return []
        }

        const allWorkstations = defaultHubs.flatMap((hub) => hub.workstations)
        const matchingWorkstation = allWorkstations.find((ws) => ws.workstationNumber === workstation.workstationNumber)

        return matchingWorkstation?.routes ?? []
    }, [isPM, defaultHubs, workstation])

    const params = useMemo(() => {
        switch (role) {
            case Role.MileageClerk: {
                const theRoutes = garage?.routes ?? []
                return [[...theRoutes].sort(routeSorter)]
            }
            case Role.PerformanceManager: {
                const theRoutes = defaultRoutes.map((route) => route.route)
                return [theRoutes.sort(routeSorter)]
            }
            default: {
                return []
            }
        }
    }, [role, garage, defaultRoutes])

    const queryKey = useMemo(() => ['mileagePeriod', formattedDate, ...params.flat(2)], [formattedDate, params])
    const routesParam = useMemo(() => queryKey.slice(2), [queryKey])

    return useAppQuery<IMileagePeriodResponse>({
        queryKey,
        queryFn: ({ queryKey }) =>
            createInstance({
                url: '/mileage-period',
                method: 'GET',
                params: {
                    Date: queryKey[1],
                    Routes: routesParam,
                },
                paramsSerializer: {
                    indexes: null,
                },
            }),
        refetchInterval: 1000 * 60, // Refetch every 60 seconds
        refetchIntervalInBackground: true, // Continue polling even if the tab is inactive
        refetchOnMount: false,
        enabled: routesParam.length > 0,
    })
}
